<script lang="ts">
  import DefaultDistributor from "@retailer/svelte/Settings/DistributorContacts/DefaultDistributor.svelte";
  import FirstTimeModal from "@retailer/svelte/Settings/DistributorContacts/FirstTimeModal.svelte";
  import Flash from "@shared/services/flashv2.js";
  import ConnectDistributorModal from "@retailer/svelte/Settings/DistributorContacts/ConnectDistributorModal/ConnectDistributorModal.svelte";
  import SalesRepModal from "@retailer/svelte/Settings/DistributorContacts/SalesRepModal.svelte";
  import SalesRepRow from "@retailer/svelte/Settings/DistributorContacts/SalesRepRow.svelte";
  import SalesReps from "@retailer/services/sales_reps.js";
  import * as _ from "lodash";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import type { Distributor, DefaultDistributors } from "@shared/types/Distributor";
  import type { SalesRep } from "@shared/types/SalesRep";
  import formatPhone from "@shared/filters/phone.js";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import OrderRequirements from "@retailer/svelte/Shared/DistributorInfo/OrderRequirements.svelte";
  import { getAddressString } from "@retailer/services/distributor_service";
  import IntegratedTooltipIcon from "@shared/svelte/Components/IntegratedTooltipIcon.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { getStockSiteLocation } from "@shared/services/retailer_user_authentication_service";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";
  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";
  import { EventTracking } from "@shared/services/event_tracking/events";

  const { trackClick } = EventTracking;

  export let Distributors;
  export let defaultDistributors: DefaultDistributors = {};
  export let assignedDistributors: Distributor[];
  export let unassignedDistributors: Distributor[];

  let user = window["$rootScope"].user;
  let showFirstTimeModal =
    !user.has_seen_sdo_explainer_modal && !localStorage.getItem("has_seen_sdo_explainer_modal");
  let selectedDistributor: Distributor;

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let authService = getControlStateAuthenticationService();

  $: distributors = $DistributorsStore;

  $: if (hasRetailerUserAuthRollout && distributors && authService) {
    for (let d of distributors) {
      d.retailerUserAuthenticationStatus = authService?.getRetailerUserAuthenticationStatus?.(
        $RetailerUserAuthenticationsStore,
        [d.id],
      );
    }
  }
  let selectedReps: SalesRep[];
  let showConnectDistributorModal: boolean = false;
  let showEditSalesRepModal: boolean = false;
  let editingSalesRep: SalesRep = null;
  let showDivisionDuplicateWarning = false;

  const submit = Distributors.submit;
  const addNewSalesRep = Distributors.addNewSalesRep;
  const removeDistributor = function (distributor) {
    Distributors.removeDistributor(distributor, function (res) {
      Flash.now({
        message: `${distributor.name} has been successfully disconnected.`,
        type: "success",
      });
      // remove distributor from distributors list
      DistributorsStore.set(
        distributors.filter(function (d) {
          return d.id !== distributor.id;
        }),
      );
    });
  };
  const updateDefaultDistributor = Distributors.updateDefaultDistributor;
  const addSalesRepErrorCallback = function (res) {
    if (res.data) {
      let msg = _.get(res, "data.errors.messages.[0]");
      if (
        msg ===
        "This sales rep already exists in our system, associated with a different distributor."
      ) {
        showDivisionDuplicateWarning = true;
      } else if (res.data.error === "Cannot manually add this sales rep.") {
        Flash.now({
          message: "Sorry - we had a problem. Please try again or contact support.",
          type: "error",
        });
      } else if (msg) {
        Flash.now({
          type: "error",
          header: "Error",
          message: msg,
        });
      }
    }
  };

  const submitErrorCallback = function () {};

  const onCloseFirstTimeModal = function () {
    Http(
      {
        method: "PUT",
        url: "/api/retailer/users/" + user.id,
        data: {
          user: {
            has_seen_sdo_explainer_modal: true,
          },
        },
      },
      function () {},
      function () {},
    );
  };

  const deleteRep = function (distributorId, rep) {
    if (!rep.id) {
      Flash.now({ type: "warning", message: "Sorry - please refresh the page and try again." });
      return;
    }
    if (!confirm("Are you sure you want to remove this contact?")) return;
    Http(
      {
        method: "DELETE",
        url: "/api/retailer/distributors/" + distributorId + "/sales_rep_assignments/" + rep.id,
      },
      function (res) {
        let msg = `We have removed ${rep.name} successfully.`;
        Flash.now({ type: "success", message: msg });
        let distributor = assignedDistributors.find(function (d) {
          return d.id === distributorId;
        });

        distributor.sales_reps = distributor.sales_reps.filter(function (r) {
          return r.id !== rep.id;
        });
        assignedDistributors = assignedDistributors;
      },
      function error(res) {
        Flash.now({ type: "warning", message: "There was an error removing this sales rep" });
        console.error(res.data);
      },
    );
  };

  function resetAndOpenConnectModal() {
    trackClick("my_distributors_connect_a_distributor_button");
    selectedDistributor = null;
    selectedReps = [];
    openConnectModal();
  }

  function openConnectModal() {
    showConnectDistributorModal = true;
  }
  function doEdit(distributor: Distributor, isFromSuggested: boolean = false) {
    if (isFromSuggested) {
      trackClick("my_distributors_add_to_my_distributors_button");
    }
    selectedDistributor = distributor;
    selectedReps = distributor.sales_reps;
    openConnectModal();
  }

  function editSalesRep(salesRep: SalesRep) {
    editingSalesRep = salesRep;
    showEditSalesRepModal = true;
  }

  const showAccountNumberFlow = (distributor: Distributor): boolean =>
    stagedRolloutCheck("connect_distributor_modal_manual_account_number") &&
    distributor.requires_account_number &&
    !distributor.is_rolling_out_integrated_orders;

  const getButtonText = (distributor: Distributor): string => {
    if (showAccountNumberFlow(distributor)) {
      if (distributor.distributor_account_id) {
        return distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID
          ? "Edit Liquor License"
          : `Edit Account #: ${distributor.account_number}`;
      } else {
        return distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID
          ? "Connect Account"
          : "Add Account Number";
      }
    } else {
      return "Add Sales Reps";
    }
  };
</script>

<style>
  .gray-600-text {
    color: #595959;
  }

  [id^="distributor_body_"] {
    overflow: visible;
  }

  .empty-state-container {
    height: 40vh;
  }
</style>

<div class="fizz-styles">
  <div class="fizz-surface fizz-card">
    <div
      class="fizz-flex-row-responsive fizz-space-between fizz-inset-vertical-24"
      class:fizz-inset-horizontal-8={!$LayoutStore.isMobile}
      class:fizz-inset-horizontal-4={$LayoutStore.isMobile}
      class:fizz-align-content-start={$LayoutStore.isMobile}
    >
      <div class="fizz-flex-item">
        <div class="fizz-text-medium">
          Provi sends orders to your distributor contacts, who can be your sales reps or customer
          service. Add and manage their contact information to ensure Provi is sending your orders
          to the right place.
        </div>
      </div>
      <button class="fizz-button-secondary" on:click={resetAndOpenConnectModal}>
        Connect a Distributor
      </button>
    </div>
  </div>

  <DefaultDistributor {defaultDistributors} {updateDefaultDistributor} />

  <div class="fizz-surface assigned-distributors">
    {#if assignedDistributors && assignedDistributors.length}
      {#each assignedDistributors as distributor}
        <div class="fizz-bordered fizz-stack-16" id="distributor_row_{distributor.id}">
          <div
            class:fizz-border-bottom={distributor.retailerUserAuthenticationStatus != "invalid"}
            class="fizz-inset-squish-24"
            id="distributor_header_{distributor.id}"
          >
            <div class="fizz-space-between-responsive fizz-align-content-start">
              <span class="fizz-full-width">
                <span class="fizz-flex-row-8 fizz-stack-8">
                  <IntegratedTooltipIcon {distributor} />
                  <UIRouterLink
                    stateName={"layout.product_listing"}
                    params={{ distributors: distributor.id }}
                    classes="fizz-heading-4 ellipsis"
                  >
                    {distributor.name}
                  </UIRouterLink>
                </span>
                <span
                  class="fizz-flex-row-8-responsive fizz-flex-wrap-8 fizz-stack-8"
                  style="color: #848484;"
                >
                  {#if distributor.contact_phone && distributor.contact_phone.length > 0}
                    <span class="fizz-no-wrap">
                      {formatPhone(distributor.contact_phone)}
                    </span>
                    <span class="fizz-hide-on-small"> &bull; </span>
                  {/if}
                  <span class="fizz-truncate" style="--max-width: 100%">
                    {getAddressString(distributor)}
                  </span>
                </span>
                <span
                  class="fizz-flex-row-8 fizz-stack-8 {$LayoutStore.isMobile
                    ? 'fizz-text-small'
                    : 'fizz-text-medium'}"
                >
                  <OrderRequirements
                    distributorName={distributor.name}
                    cutoffTime={distributor.next_cutoff}
                    minPurchase={distributor.minimum_dollars_per_order}
                    minQuantity={distributor.minimum_quantity_per_order}
                    notes={distributor.ordering_notes}
                    isMobile={$LayoutStore.isMobile}
                  />
                </span>
              </span>
              <div class="fizz-flex-row-8">
                {#if !distributor.retailerUserAuthenticationStatus || (distributor.retailerUserAuthenticationStatus == "valid" && distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID)}
                  <button
                    data-testid="distributor-edit-button"
                    class="fizz-button fizz-button-small"
                    on:click={() => doEdit(distributor)}
                  >
                    {getButtonText(distributor)}
                  </button>
                {:else if distributor.retailerUserAuthenticationStatus == "pending"}
                  <button
                    data-testid="distributor-wsc-connect"
                    class="fizz-button fizz-button-small"
                    on:click={() => authService?.startVerification?.("pending")}
                  >
                    {authService?.connectCta}
                  </button>
                {/if}
                <button
                  class="fizz-button-danger fizz-button-small"
                  on:click={() => removeDistributor(distributor)}
                >
                  Remove
                </button>
              </div>
            </div>

            {#if distributor.retailerUserAuthenticationStatus == "invalid" && authService?.bannerComponent}
              <svelte:component
                this={authService?.bannerComponent}
                authStatus={distributor.retailerUserAuthenticationStatus}
                {authService}
              />
            {/if}
          </div>
          {#if distributor.retailerUserAuthenticationStatus != "invalid"}
            <div class="fizz-card" id="distributor_body_{distributor.id}">
              {#if distributor.retailerUserAuthenticationStatus == "pending"}
                <div class="fizz-card-padded">
                  <div class="fizz-flex-row fizz-justify-center">
                    <p class="gray-600-text">You have not connected your agency store</p>
                  </div>
                </div>
              {:else if distributor.retailerUserAuthenticationStatus == "valid" && distributor.id === OHLQ_DISTRIBUTOR_ID}
                <div class="fizz-flex-row-responsive fizz-card-padded">
                  {#await getStockSiteLocation(distributor.id, distributor.account_number) then data}
                    <div class="fizz-flex-item fizz-flex-row fizz-space-between"
                      >{data.name || ""}</div
                    >
                    <div class="fizz-flex-item"> {data.storePhone || ""} </div>
                  {/await}
                </div>
              {:else if distributor.sales_reps && distributor.sales_reps.length > 0}
                {#each distributor.sales_reps as salesRep}
                  <SalesRepRow
                    {deleteRep}
                    {editSalesRep}
                    {salesRep}
                    distributorId={distributor.id}
                    hideDeleteButton={showAccountNumberFlow(distributor)}
                    hideEditButton={showAccountNumberFlow(distributor)}
                  />
                {/each}
              {:else}
                <div class="fizz-card-padded">
                  <div class="fizz-flex-row fizz-justify-center">
                    <p class="gray-600-text">You don't have any sales reps</p>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    {:else}
      <div
        class="fizz-flex-row fizz-justify-center fizz-bordered empty-state-container fizz-inset-vertical-12"
      >
        <div>
          <div class="fizz-flex-row fizz-justify-center">
            <Icon type="Truck" height="44" width="44" color="darkgray" />
          </div>
          <div>
            <div class="fizz-text-center fizz-text-large fizz-text-strong fizz-stack-8">
              You don't have any distributors!
            </div>
            <div class="fizz-text-center fizz-text-medium">
              To place orders add your distributors and sales reps
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>

  {#if unassignedDistributors.length}
    <div class="suggested-distributors">
      <div class="background-white padding--small margin-bottom--smallest">
        <div class="grid-row">
          <div class="grid-col c8-12--md c12-12 vertical-align-middle">
            <div class="align-center align-left-md default-title--bold margin-bottom--smallest">
              Suggested Distributors
            </div>
          </div>
        </div>
      </div>
      {#each unassignedDistributors as distributor}
        <div class="background-white padding--small" id="suggested_distributor_{distributor.id}">
          <div class="grid-row">
            <div class="grid-col c6-12">
              <div class="default-body--bold ellipsis">
                {distributor.name}
              </div>
              <div class="default-body--regular ellipsis">
                {#if distributor.city}
                  {distributor.city},
                {/if}
                {distributor.state}
              </div>
            </div>
            <div class="grid-col c6-12 margin-top--smallest">
              <button
                class="button button--secondary fizz-button-small float-right"
                on:click={() => doEdit(distributor, true)}
              >
                Add to My Distributors
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<FirstTimeModal {showFirstTimeModal} {onCloseFirstTimeModal} />
<ConnectDistributorModal
  bind:showConnectDistributorModal
  {submit}
  {selectedDistributor}
  {selectedReps}
  {addSalesRepErrorCallback}
  {addNewSalesRep}
  {submitErrorCallback}
  {showDivisionDuplicateWarning}
  getReps={SalesReps.getReps}
  providedDistributor={!!selectedDistributor}
/>
<SalesRepModal bind:show={showEditSalesRepModal} bind:salesRep={editingSalesRep} />
